import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, Users, Building, Shield, Rocket, BookOpen, Briefcase, Network as NetworkIcon } from "lucide-react";
import { cn } from "@/lib/utils";

const About: React.FC = () => {
  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-16 md:py-24">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="text-center mb-20"
          >
            <div className="inline-flex items-center rounded-full border border-border px-4 py-2 text-sm mb-6 bg-transparent">
              <span className="text-foreground">About Us</span>
              <div className="mx-1 h-1 w-1 rounded-full bg-muted"></div>
              <span className="text-muted-foreground">Our Story</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold mb-6">
              About <span className="text-blue-400">Babbage</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              A leading blockchain company specializing in distributed identity solutions
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
            className="space-y-12 text-muted-foreground"
          >
            <p className="text-xl md:text-2xl font-medium text-foreground mb-12 leading-relaxed">
              Babbage is a leading blockchain company specializing in distributed identity solutions. Built on a foundation of open-source principles, our goal is to create a more efficient, scalable, and secure internet by empowering users to own their own identities and data.
            </p>

            {/* Added Mission Statement */}
            <motion.div
              className="border-l-4 border-primary pl-6 py-4 my-12 bg-muted/50 rounded-r-lg"
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
            >
              <p className="text-lg italic text-foreground">
                [ Mission: Empowering individuals and organizations through decentralized systems built on open source. ]
              </p>
            </motion.div>

            <div className="grid md:grid-cols-2 gap-8 my-12">
              <div className="bg-card rounded-xl p-6 border border-border">
                <Shield className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Privacy First</h3>
                <p className="text-muted-foreground">
                  We believe that the current model of centralized identity systems is prone to security failures, data breaches, and invasions of privacy.
                </p>
              </div>
              <div className="bg-card rounded-xl p-6 border border-border">
                <Rocket className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Innovation Driven</h3>
                <p className="text-muted-foreground">
                  By introducing a micropayment-based monetization model built upon the original Bitcoin protocol from 2009, we aim to create a scalable platform for the next generation.
                </p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Our technology addresses this problem by creating a new incentive model that puts users in charge and facilitates a direct, purely peer-to-peer solution. We believe this will lead to a more efficient and privacy-respecting internet, where users are free to move between platforms and make informed choices about how their data is used.
            </p>

            <p className="text-muted-foreground leading-relaxed">
              Our team is made up of experienced professionals with a passion for blockchain and a commitment to building a better internet. We are constantly pushing the boundaries of what is possible with our technology, and we are always looking for new ways to improve our products and services.
            </p>

            <div className="my-16 bg-card rounded-xl p-8 border border-border">
              <p className="text-lg text-foreground italic">
                "We are excited to be at the forefront of the distributed identity revolution, and we look forward to working with you to create a brighter future for all."
              </p>
            </div>

            {/* Video Section */}
            <div className="my-16">
              <h2 className="text-2xl font-bold mb-6 text-center text-foreground">See Babbage in Action</h2>
              <div className="relative w-full rounded-xl overflow-hidden shadow-2xl" style={{ paddingBottom: '56.25%' }}>
                <iframe
                  src="https://www.youtube.com/embed/Achay2OhurY"
                  title="Babbage Introduction"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  className="absolute top-0 left-0 w-full h-full"
                ></iframe>
              </div>
            </div>

            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold text-foreground mb-6">
                <Building className="h-6 w-6 text-blue-400" />
                Our Company
              </h2>
              <p className="text-muted-foreground mb-8">
                In March of 2020, we established Peer-to-peer Privacy Systems Research LLC in the great state of Oregon to start developing the intellectual property underpinning Babbage.
              </p>
            </div>

            <div className="bg-card rounded-xl p-8 border border-border">
              <h3 className="text-xl font-semibold mb-4 text-foreground">Our Culture</h3>
              <p className="text-muted-foreground">
                At Babbage HQ, we value and cultivate a culture of meritocracy where team members are encouraged to speak their minds and contribute their best ideas, regardless of their background or position within the organization. We believe that the best ideas should rise to the top based on their merit, and that everyone should be given equal opportunity to contribute and be recognized for their contributions.
              </p>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              The culture of meritocracy is reflected in our decision-making processes and in the way we recognize and reward team members for their contributions. We are committed to building a diverse and inclusive team where everyone is valued and given the opportunity to succeed based on their skills and abilities.
            </p>

            <div className="bg-card rounded-xl p-8 border border-border">
              <p className="text-foreground">
                Peer-to-peer Privacy Systems Research, LLC values individual privacy and autonomy. We believe that individuals have the right to control their own personal information and data, and that centralized systems of control are inherently prone to abuse.
              </p>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              Our technology empowers individuals to take back control of their own identity and data, and to engage in online interactions with confidence in their privacy. We believe that this approach aligns with personal responsibility and limited government intervention.
            </p>

            <div>
              <h2 className="flex items-center gap-3 text-2xl font-bold text-foreground mb-6">
                <Users className="h-6 w-6 text-blue-400" />
                Leadership
              </h2>
              <div className="bg-card rounded-xl p-8 border border-border">
                <h3 className="text-xl font-semibold mb-2 text-foreground">Ty Everett</h3>
                <p className="text-sm text-muted-foreground mb-4">Founding President</p>
                <p className="text-muted-foreground">
                  Prior to becoming President, Ty worked in the Bitcoin and blockchain industry as a software developer specializing in cryptography. In 2018, he founded the Gateway payment processing platform to accelerate merchant adoption of Bitcoin. In 2019, he lead a team working on a decentralized Bitcoin wallet.
                </p>
              </div>
            </div>

            <p className="text-muted-foreground leading-relaxed">
              The micropayment ecosystem created by Babbage is a natural evolution of Bitcoin's core philosophy, something Ty is uniquely equipped to develop and scale.
            </p>
          </motion.div>

          {/* Offerings Section */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.15 }}
            className="mt-16"
          >
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Our Offerings</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {/* Metanet Engineering Curriculum */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <BookOpen className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Metanet Engineering Curriculum</h3>
                <p className="text-muted-foreground text-sm">
                  Comprehensive educational curriculum designed to equip engineers with in-depth blockchain skills.
                </p>
              </div>
              {/* Blockchain Training for Enterprise Engineers */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <BookOpen className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Enterprise Blockchain Training</h3>
                <p className="text-muted-foreground text-sm">
                  Tailored training programs designed to upskill engineers for implementing and managing blockchain technologies within enterprise environments.
                </p>
              </div>
              {/* Skilled Industries */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <Briefcase className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Skilled Industries Solutions</h3>
                <p className="text-muted-foreground text-sm">
                  From digital logbooks for professional divers to tracing car parts on the assembly line, we got you covered!
                </p>
              </div>
              {/* Blockchain Integration / Network Architecture consultancy */}
              <div className="bg-card rounded-xl p-6 border border-border">
                <NetworkIcon className="h-8 w-8 text-blue-400 mb-4" />
                <h3 className="text-lg font-semibold mb-2 text-foreground">Integration & Architecture Consultancy</h3>
                <p className="text-muted-foreground text-sm">
                  Pioneering research into scalable, resilient base layer systems backed by world class expertise.
                </p>
              </div>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="text-center bg-card rounded-xl p-8 border border-border mt-16"
          >
            <h2 className="text-2xl font-bold mb-6 text-foreground">Join Our Community</h2>
            <p className="text-muted-foreground mb-8 max-w-2xl mx-auto">
              Be part of the revolution in Bitcoin application development. Connect with our community of developers, contributors, and enthusiasts.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button
                size="lg"
                className="group bg-blue-500 hover:bg-blue-600 text-white"
                onClick={() => window.open('https://join.slack.com/t/projectbabbage/shared_invite/zt-29gkwwaoj-yFqgWEC4YT_Sq7eH5Expsw', '_blank')}
              >
                Join Slack
                <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Button>
              <Button
                variant="outline"
                size="lg"
                className="border-border text-foreground hover:bg-muted"
                onClick={() => window.open('https://github.com/p2ppsr', '_blank')}
              >
                GitHub
              </Button>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default About; 